/**
 * AuthContext - Authentication Context for PeoplePulse
 * 
 * Provides authentication state including user identity, roles, and permissions
 * resolved from Azure AD group memberships.
 * 
 * Requirements: 7.1 - Frontend receives user's resolved roles and permissions
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { apiService } from '../services/apiService';

// Permission enum matching backend
export const Permission = {
  // Employee management
  VIEW_EMPLOYEES: 'view_employees',
  CREATE_EMPLOYEES: 'create_employees',
  EDIT_EMPLOYEES: 'edit_employees',
  DELETE_EMPLOYEES: 'delete_employees',
  
  // Absence management
  VIEW_ABSENCES: 'view_absences',
  CREATE_ABSENCES: 'create_absences',
  APPROVE_ABSENCES: 'approve_absences',
  DELETE_ABSENCES: 'delete_absences',
  
  // Observations
  VIEW_OBSERVATIONS: 'view_observations',
  CREATE_OBSERVATIONS: 'create_observations',
  EDIT_OBSERVATIONS: 'edit_observations',
  DELETE_OBSERVATIONS: 'delete_observations',
  
  // Reports
  VIEW_REPORTS: 'view_reports',
  CREATE_REPORTS: 'create_reports',
  EXPORT_REPORTS: 'export_reports',
  
  // System administration
  MANAGE_USERS: 'manage_users',
  MANAGE_SYSTEM: 'manage_system',
  VIEW_AUDIT_LOGS: 'view_audit_logs',
  MANAGE_INTEGRATIONS: 'manage_integrations',
};

// UserRole enum matching backend
export const UserRole = {
  SUPER_ADMIN: 'super_admin',
  HR_ADMIN: 'hr_admin',
  SCHOOL_ADMIN: 'school_admin',
  TEACHER: 'teacher',
  SUBSTITUTE: 'substitute',
  READONLY: 'readonly',
};

// Auth context shape
const AuthContext = createContext({
  user: null,
  roles: [],
  permissions: [],
  isLoading: true,
  error: null,
  hasPermission: () => false,
  hasAnyPermission: () => false,
  hasAllPermissions: () => false,
  hasRole: () => false,
  refreshRoles: async () => {},
});

/**
 * AuthProvider component that wraps the application and provides auth state
 */
export function AuthProvider({ children }) {
  const { instance, accounts } = useMsal();
  const [authState, setAuthState] = useState({
    user: null,
    roles: [],
    permissions: [],
    isLoading: true,
    error: null,
  });

  const account = accounts[0];

  /**
   * Fetch user roles and permissions from the backend
   * Requirement: 7.1 - Frontend receives resolved roles and permissions
   */
  const fetchUserRoles = useCallback(async () => {
    if (!account) {
      setAuthState(prev => ({
        ...prev,
        user: null,
        roles: [],
        permissions: [],
        isLoading: false,
        error: null,
      }));
      return;
    }

    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

      console.log('🔐 AuthContext: Fetching user roles from backend...');
      console.log('🔐 AuthContext: Account:', account.username);

      // Fetch current user info with roles from backend using apiService
      // Requirement: 7.1, 7.4 - Get roles and permissions from backend
      const userData = await apiService.getCurrentUser();

      console.log('✅ AuthContext: Received user data from backend:', {
        oid: userData.oid,
        email: userData.email,
        roles: userData.roles,
        permissions: userData.permissions?.length || 0,
        sourceGroups: userData.source_groups?.length || 0,
      });

      setAuthState({
        user: {
          oid: userData.oid,
          email: userData.email,
          name: userData.name,
          tenantId: userData.tenant_id,
          sourceGroups: userData.source_groups || [],
        },
        roles: userData.roles || [],
        permissions: userData.permissions || [],
        isLoading: false,
        error: null,
      });
    } catch (error) {
      console.error('❌ AuthContext: Failed to fetch user roles:', error);
      console.error('❌ AuthContext: Error details:', error.message);
      
      // Fallback: use account info from MSAL
      console.warn('⚠️ AuthContext: Using fallback - no roles from backend');
      setAuthState({
        user: {
          oid: account.localAccountId,
          email: account.username,
          name: account.name || account.username,
          tenantId: account.tenantId,
          sourceGroups: [],
        },
        roles: [],
        permissions: [],
        isLoading: false,
        error: error.message,
      });
    }
  }, [account]);

  // Fetch roles on mount and when account changes
  useEffect(() => {
    fetchUserRoles();
  }, [fetchUserRoles]);

  /**
   * Check if user has a specific permission
   * Requirement: 7.2, 7.3 - Frontend uses permissions for UI rendering
   */
  const hasPermission = useCallback(
    (permission) => {
      return authState.permissions.includes(permission);
    },
    [authState.permissions]
  );

  /**
   * Check if user has any of the specified permissions (OR logic)
   */
  const hasAnyPermission = useCallback(
    (permissions) => {
      return permissions.some((p) => authState.permissions.includes(p));
    },
    [authState.permissions]
  );

  /**
   * Check if user has all of the specified permissions (AND logic)
   */
  const hasAllPermissions = useCallback(
    (permissions) => {
      return permissions.every((p) => authState.permissions.includes(p));
    },
    [authState.permissions]
  );

  /**
   * Check if user has a specific role
   */
  const hasRole = useCallback(
    (role) => {
      return authState.roles.includes(role);
    },
    [authState.roles]
  );

  /**
   * Refresh user roles from backend
   * Requirement: 7.4 - Frontend provides method to refresh role information
   */
  const refreshRoles = useCallback(async () => {
    await fetchUserRoles();
  }, [fetchUserRoles]);

  const contextValue = {
    ...authState,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    refreshRoles,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Custom hook to access auth context
 * 
 * Usage:
 *   const { user, roles, permissions, hasPermission } = useAuth();
 *   if (hasPermission(Permission.VIEW_EMPLOYEES)) { ... }
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
